// src/pages/inventory/RapDecisions.js
import React from 'react';
import dayjs from 'dayjs';
import {
  Box, Paper, TextField, MenuItem, Button,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Divider
} from '@mui/material';
import { Autocomplete } from '@mui/material';

import Client from '../Client';
import Config from '../ClientConfig';

const compactFieldSx = {
  minWidth: {
    xs: 'calc(50% - 6px)',
    sm: 150,
  },
  flex: {
    xs: '1 1 calc(50% - 6px)',
    sm: '0 0 auto',
  },
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};

const compactFilterPaperSx = {
  flexShrink: 0,
  p: {
    xs: 0.5,
    sm: 0.75,
  },
  mb: 0.75,
  borderRadius: '14px',
  overflow: 'hidden',
};

const compactTablePaperSx = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '14px',
};

const compactTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  width: '100%',
};

const compactTableSx = {
  minWidth: {
    xs: 860,
    sm: 980,
    md: '100%',
  },
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.45, sm: 0.65 },
    px: { xs: 0.5, sm: 0.9 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
    whiteSpace: 'nowrap',
  },
};

const compactPaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

class RapDecisions extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),

      // Lookups used by Decisions
      locations: [],            // [{ iref, name, ... }]
      products: [],             // [{ iref/code, name, ... }]
      productNameMap: {},       // { productIdOrCode: name }

      // Decisions filters
      decisionsFilters: {
        period: dayjs().format('YYYY-MM'),
        location: '',
        product: '',
      },

      // Decisions data
      decisionsAll: [],
      decisionsRows: [],

      // UI state
      decisionsPage: 0,
      decisionsRpp: -1,

      // Loading flags
      decisionsLoaded: false,
      decisionsLoading: false,
    };
  }

  componentDidMount() {
    this.fetchLookups();
  }

  // ------- utils -------
  formatQty = (val) => {
    if (val == null || val === '') return '';

    const num = Number(val);

    if (!Number.isFinite(num)) return val;

    const rounded = Math.round(num * 1000) / 1000; // 3 decimals

    return rounded.toString(); // strip trailing zeros
  };

  // decisions query (year/month + optional location; product filtered locally)
  buildDecisionsQuery = () => {
    const { period, location } = this.state.decisionsFilters;
    const [year, month] = (period || '').split('-');

    const q = { year, month };

    if (location) q.location = location;

    return q;
  };

  // ------- lookups (locations + products) -------
  fetchLookups() {
    const { client } = this.state;

    const locationsResp = client.getData(Config.API_URL_LOCATIONS + 'list_locations/', { status: 1 }) || {};
    const productsResp  = client.getData(Config.API_URL_PRODUCT + 'list_products/', { status: 2, orderBy_name: true }) || {};

    const locations = Array.isArray(locationsResp.locations) ? locationsResp.locations : [];
    locations.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const products = Array.isArray(productsResp.products) ? productsResp.products : [];

    const productNameMap = {};

    products.forEach((p) => {
      const pid = p.iref || p.uid || p.code || p.product || p.id;
      const pname = p.name || p.productName || p.description || p.code || pid;

      if (pid) productNameMap[String(pid)] = String(pname);
    });

    this.setState(
      { locations, products, productNameMap },
      () => this.fetchDecisions()
    );
  }

  // ------- decisions fetch -------
  fetchDecisions = () => {
    const { client, decisionsFilters } = this.state;

    this.setState({ decisionsLoading: true, decisionsPage: 0 });

    const query = this.buildDecisionsQuery(); // { year, month, location? }
    const resp = client.getData(`${Config.API_URL_REPORTS_TRANSACTIONS}month_decisions/`, query) || {};
    const list = Array.isArray(resp.results) ? resp.results : [];

    const prod = decisionsFilters.product || '';
    const filtered = prod
      ? list.filter(r => String(r.productIref) === String(prod))
      : list;

    this.setState({
      decisionsAll: list,
      decisionsRows: filtered,
      decisionsLoading: false,
      decisionsLoaded: true,
    });
  };

  exportCSV = () => {
    const { decisionsRows, productNameMap } = this.state;

    if (!decisionsRows || !decisionsRows.length) return;

    const header = ['Date', 'Location', 'Product', 'Source', 'Calc Qty', 'Decision Qty', 'Delta', 'Doc No'];

    const lines = decisionsRows.map(r => {
      const locationLabel = r.locationName || r.locationIref;
      const productLabel = r.productName || productNameMap[String(r.productIref)] || r.productIref;
      const sourceText = r.source === 0 ? 'Calculated' : r.source === 1 ? 'Inventory' : 'Manual';

      return [
        `"${dayjs(r.date).format('YYYY-MM-DD')}"`,
        `"${locationLabel}"`,
        `"${productLabel}"`,
        `"${sourceText}"`,
        this.formatQty(r.calcQty),
        this.formatQty(r.decisionQty),
        this.formatQty(r.delta),
        `"${r.docNo || ''}"`,
      ].join(',');
    });

    const csv = "\uFEFF" + [header.join(','), ...lines].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `decisions_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  // ------- UI: filters -------
  renderDecisionsFilters = () => {
    const { decisionsFilters, locations, products } = this.state;

    const productOptions = (products || []).map(p => ({
      value: String(p.iref || p.id || p.uid || p.code),
      label: p.name || p.productName || p.description || p.code || String(p.iref || p.id || p.uid || p.code),
    }));

    const productValue = productOptions.find(opt => opt.value === String(decisionsFilters.product)) || null;

    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 0.75, sm: 1 },
            flexWrap: 'wrap',
            alignItems: 'center',
            '& .MuiButton-root': compactButtonSx,
          }}
        >
          <TextField
            label="Period"
            type="month"
            value={decisionsFilters.period}
            onChange={(e) =>
              this.setState({
                decisionsFilters: {
                  ...decisionsFilters,
                  period: e.target.value,
                },
              })
            }
            size="small"
            margin="dense"
            sx={compactFieldSx}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            select
            label="Location"
            value={decisionsFilters.location}
            onChange={(e) =>
              this.setState({
                decisionsFilters: {
                  ...decisionsFilters,
                  location: e.target.value,
                },
              })
            }
            size="small"
            margin="dense"
            sx={compactFieldSx}
          >
            <MenuItem value="">All</MenuItem>

            {locations.map((loc) => (
              <MenuItem
                key={String(loc.iref || loc.uid || loc.id)}
                value={String(loc.iref || loc.uid || loc.id)}
              >
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1, minWidth: 8 }} />

          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              this.setState({
                decisionsFilters: {
                  period: dayjs().format('YYYY-MM'),
                  location: '',
                  product: '',
                },
              }, () => this.fetchDecisions())
            }
          >
            Reset
          </Button>

          <Button variant="contained" size="small" onClick={this.fetchDecisions}>
            Apply
          </Button>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              mx: { xs: 0, sm: 0.75 },
              display: { xs: 'none', sm: 'block' },
            }}
          />

          <Autocomplete
            size="small"
            value={productValue}
            options={productOptions}
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '0 0 260px',
              },
              minWidth: 0,
              maxWidth: {
                xs: '100%',
                sm: 340,
              },
            }}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_e, opt) => {
              const newVal = opt ? opt.value : '';

              this.setState((prev) => {
                const all = prev.decisionsAll || [];
                const rows = newVal
                  ? all.filter(r => String(r.productIref) === String(newVal))
                  : all;

                return {
                  decisionsFilters: {
                    ...prev.decisionsFilters,
                    product: newVal,
                  },
                  decisionsRows: rows,
                  decisionsPage: 0,
                };
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                margin="dense"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    minHeight: 34,
                    borderRadius: '12px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.84rem',
                    },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                  },
                }}
              />
            )}
          />

          <Button
            variant="outlined"
            size="small"
            onClick={this.exportCSV}
            disabled={!this.state.decisionsRows.length}
          >
            CSV
          </Button>
        </Box>
      </Paper>
    );
  };

  // ------- UI: table -------
  renderDecisionsTable = () => {
    const {
      decisionsRows,
      decisionsLoading,
      decisionsPage,
      decisionsRpp,
      productNameMap,
    } = this.state;

    const paged = decisionsRpp > 0
      ? decisionsRows.slice(decisionsPage * decisionsRpp, decisionsPage * decisionsRpp + decisionsRpp)
      : decisionsRows;

    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Calc Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Decision Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Δ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Doc No</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((r) => {
                const locationLabel = r.locationName || r.locationIref;
                const productLabel = r.productName || productNameMap[String(r.productIref)] || r.productIref;
                const deltaColor = Number(r.delta) > 0 ? 'green' : Number(r.delta) < 0 ? 'red' : undefined;
                const sourceText = r.source === 0 ? 'Calculated' : r.source === 1 ? 'Inventory' : 'Manual';

                return (
                  <TableRow key={r.iref} hover>
                    <TableCell>{dayjs(r.date).format('DD MMM YYYY')}</TableCell>

                    <TableCell title={r.locationIref}>
                      {locationLabel}
                    </TableCell>

                    <TableCell title={r.productIref}>
                      {productLabel}
                    </TableCell>

                    <TableCell align="right">
                      {sourceText}
                    </TableCell>

                    <TableCell align="right">
                      {this.formatQty(r.calcQty)}
                    </TableCell>

                    <TableCell align="right">
                      {this.formatQty(r.decisionQty)}
                    </TableCell>

                    <TableCell align="right" sx={{ color: deltaColor }}>
                      {this.formatQty(r.delta)}
                    </TableCell>

                    <TableCell>
                      {r.docNo || ''}
                    </TableCell>
                  </TableRow>
                );
              })}

              {!paged.length && !decisionsLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={decisionsRows.length}
          page={decisionsPage}
          onPageChange={(_e, p) => this.setState({ decisionsPage: p })}
          rowsPerPage={decisionsRpp}
          onRowsPerPageChange={(e) =>
            this.setState({
              decisionsRpp: Number(e.target.value),
              decisionsPage: 0,
            })
          }
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={compactPaginationSx}
        />

        {decisionsLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 5,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  };

  // ------- render -------
  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderDecisionsFilters()}
        {this.renderDecisionsTable()}
      </Box>
    );
  }
}

export default RapDecisions;